import Logger from '../lib/logger'

/**
 * Toast utility - Centralized toast notifications using event dispatch.
 *
 * Works with the built-in ToastController by dispatching a 'toast:show' event
 * on window. This eliminates the need for controllers to reimplement toast logic.
 *
 * Usage:
 *   import { showToast, showSuccess, showError, showWarning, showInfo } from '../utils/toast'
 *   showToast('Saved!', 'success')
 *   showError('Something went wrong')
 */

/**
 * Show a toast notification via the toast controller event system.
 * @param {string} message - The message to display
 * @param {'success'|'error'|'warning'|'info'|'primary'} type - Toast type (defaults to 'info')
 * @param {number|null} [duration] - Optional auto-dismiss duration in ms (null uses controller default)
 * @returns {void}
 */
export function showToast(message, type = 'info', duration = null) {
  try {
    window.dispatchEvent(new CustomEvent('toast:show', {
      detail: { message, type, duration }
    }))
  } catch (error) {
    Logger.error('Failed to dispatch toast event', error)
  }
}

/**
 * Show a success toast.
 */
export function showSuccess(message, duration) {
  return showToast(message, 'success', duration)
}

/**
 * Show an error toast.
 */
export function showError(message, duration) {
  return showToast(message, 'error', duration)
}

/**
 * Show a warning toast.
 */
export function showWarning(message, duration) {
  return showToast(message, 'warning', duration)
}

/**
 * Show an info toast.
 */
export function showInfo(message, duration) {
  return showToast(message, 'info', duration)
}

export default {
  showToast,
  showSuccess,
  showError,
  showWarning,
  showInfo
}
